/**
 * @file SharePoint user and group membership operations
 * Pure SharePoint REST API interactions for user management
 */

/**
 * Get current user information from SharePoint
 * @param {object} spClient - SharePoint client instance
 * @returns {Promise<object>} User data with id, name, email
 */
export async function getCurrentUser(spClient) {
  const response = await spClient.request('web/currentuser', 'GET');
  const userData = response.d;

  if (!userData || !userData.Id) {
    throw new Error('SharePoint did not return valid user data');
  }

  if (!userData.Title) {
    throw new Error('SharePoint user data missing required Title field');
  }

  return {
    id: userData.LoginName,
    name: userData.Title,
    email: userData.Email || '',
  };
}

/**
 * Get current user's SharePoint group memberships
 * @param {object} spClient - SharePoint client instance
 * @returns {Promise<Array>} Array of group objects with Id, Title
 */
export async function getCurrentUserGroups(spClient) {
  const response = await spClient.request('web/currentuser/groups', 'GET');
  return response.d?.results || [];
}

/**
 * Get all SharePoint groups
 * @param {object} spClient - SharePoint client instance
 * @returns {Promise<Array>} Array of all site groups
 */
export async function getAllGroups(spClient) {
  const response = await spClient.request('web/sitegroups', 'GET');
  return response.d?.results || [];
}

/**
 * Add user to SharePoint group
 * @param {object} spClient - SharePoint client instance
 * @param {number} groupId - SharePoint group ID (numeric)
 * @param {string} loginName - User login name to add (required)
 * @returns {Promise<void>}
 */
export async function addUserToGroup(spClient, groupId, loginName) {
  if (!loginName) {
    throw new Error('loginName is required for addUserToGroup');
  }

  await spClient.request(
    `web/sitegroups(${groupId})/users`,
    'POST',
    {
      headers: { 'Content-Type': 'application/json;odata=verbose' },
      body: JSON.stringify({
        __metadata: { type: 'SP.User' },
        LoginName: loginName,
      }),
    },
    true
  );
}

/**
 * Remove user from SharePoint group
 * @param {object} spClient - SharePoint client instance
 * @param {number} groupId - SharePoint group ID (numeric)
 * @param {string} loginName - User login name to remove (required)
 * @returns {Promise<void>}
 */
export async function removeUserFromGroup(spClient, groupId, loginName) {
  if (!loginName) {
    throw new Error('loginName is required for removeUserFromGroup');
  }

  // SharePoint-specific encoding: encode everything but keep backslashes unencoded
  // for domain separators in claim-based login names
  const encodedLoginName = encodeURIComponent(loginName).replace(/%5C/g, '\\');

  await spClient.request(
    `web/sitegroups(${groupId})/users/removeByLoginName(@loginName)?@loginName='${encodedLoginName}'`,
    'POST',
    {},
    true
  );
}

/**
 * Check if user is member of specific group
 * @param {object} spClient - SharePoint client instance
 * @param {number} groupId - SharePoint group ID (numeric)
 * @param {string} userId - User ID to check (defaults to current user)
 * @returns {Promise<boolean>} True if user is member
 */
export async function isUserInGroup(spClient, groupId, userId = null) {
  const userGroups = await getCurrentUserGroups(spClient);
  return userGroups.some(group => group.Id === groupId);
}
